import { MONITORING_AGENT_REGISTRY } from 'constants/monitoringAgents';
import { formatSessionChromeTitle, getSessionTemplateTitle } from 'utils/sessionTemplateTitle';

import {
  isMonitoringSessionDoneStatus,
  isMonitoringSessionProcessingStatus,
  isMonitoringSessionType,
} from './monitoringSessionFlowUtils';

import type IMonitoringAgentDefinition from 'constants/interfaces/IMonitoringAgentDefinition';
import type ISessionVCItem from 'store/slices/valueChainMap/interfaces/ISessionVCItem';

export interface IMonitoringSessionCardView {
  session: ISessionVCItem,
  title: string,
  entities: {
    id: number,
    name: string,
    type?: string,
  }[],
}

export const isMonitoringGeneratingCarouselSession = (session: ISessionVCItem): boolean => {
  if (!isMonitoringSessionType(session.type)) {
    return false;
  }

  if (isMonitoringSessionProcessingStatus(session.status)) {
    return true;
  }

  return Boolean(session.isActive) && isMonitoringSessionDoneStatus(session.status);
};

export const selectMonitoringGeneratingCarouselSessions = (
  sessions?: ISessionVCItem[],
): ISessionVCItem[] => {
  const typeOrder = MONITORING_AGENT_REGISTRY.map((agent) => agent.sessionType);

  return (sessions || [])
    .filter(isMonitoringGeneratingCarouselSession)
    .sort((left, right) => {
      const leftOrder = typeOrder.indexOf(left.type);
      const rightOrder = typeOrder.indexOf(right.type);
      const normalizedLeft = leftOrder === -1 ? typeOrder.length : leftOrder;
      const normalizedRight = rightOrder === -1 ? typeOrder.length : rightOrder;

      if (normalizedLeft !== normalizedRight) {
        return normalizedLeft - normalizedRight;
      }

      return right.id - left.id;
    });
};

export const filterMonitoringSessionsByType = (
  sessions: ISessionVCItem[],
  sessionType: string,
): ISessionVCItem[] => (
  sessions.filter((sessionItem) => sessionItem.type === sessionType)
);

export const resolveExistingMonitoringSession = ({
  sessions,
  sessionId,
  activeAgentSession,
  sessionType,
}: {
  sessions?: ISessionVCItem[],
  sessionId?: number,
  activeAgentSession?: ISessionVCItem,
  sessionType?: string,
}): ISessionVCItem | undefined => {
  if (!sessionType) {
    return undefined;
  }

  const matchesCurrentAgent = (sessionItem?: ISessionVCItem): sessionItem is ISessionVCItem => (
    sessionItem != null && sessionItem.type === sessionType
  );

  const activeSessionId = sessionId || activeAgentSession?.id;

  if (activeSessionId) {
    const matchedSession = sessions?.find(
      (sessionItem) => sessionItem.id === activeSessionId && matchesCurrentAgent(sessionItem),
    );

    if (matchedSession) {
      return matchedSession;
    }

    if (activeAgentSession?.id === activeSessionId && matchesCurrentAgent(activeAgentSession)) {
      return activeAgentSession;
    }

    return undefined;
  }

  return sessions?.find(
    (sessionItem) => matchesCurrentAgent(sessionItem) && sessionItem.isActive,
  ) || sessions?.find(matchesCurrentAgent);
};

/** New selection must not pass the list fallback session into AICardPanel (ATT-110 / ATT-137). */
export const resolveMonitoringCardPanelExistingSession = ({
  isNewMonitoringSelection,
  monitoringCardPanelFlow,
  existingMonitoringSession,
  sessionType,
}: {
  isNewMonitoringSelection: boolean,
  monitoringCardPanelFlow: string,
  existingMonitoringSession?: ISessionVCItem,
  sessionType?: string,
}): ISessionVCItem | undefined => {
  if (isNewMonitoringSelection || monitoringCardPanelFlow !== 'manual-session') {
    return undefined;
  }

  if (!existingMonitoringSession || existingMonitoringSession.type !== sessionType) {
    return undefined;
  }

  return existingMonitoringSession;
};

export const buildMonitoringSessionTitle = ({
  session,
  agentDefinition,
  formatTitle,
  formatDate,
}: {
  session: ISessionVCItem,
  agentDefinition: IMonitoringAgentDefinition,
  formatTitle: (key: string) => string,
  formatDate: (dateValue?: string) => string,
}): string => {
  const titleKey = session.isActive
    ? `${agentDefinition.sessionTitleKey}.current`
    : `${agentDefinition.sessionTitleKey}.previous`;

  return formatSessionChromeTitle({
    baseTitle: formatTitle(titleKey),
    templateTitle: getSessionTemplateTitle(session),
    formattedDate: formatDate(session.updatedAt || session.createdAt),
  });
};

export const filterMonitoringSessionCardsBySearch = (
  sessionCards: IMonitoringSessionCardView[],
  search: string,
): IMonitoringSessionCardView[] => {
  const searchLower = search.trim().toLowerCase();

  if (!searchLower) {
    return sessionCards;
  }

  return sessionCards.filter(({ title, entities }) => {
    const titleMatches = title.toLowerCase().includes(searchLower);
    const entityMatches = entities.some((entity) => entity.name.toLowerCase().includes(searchLower));

    return titleMatches || entityMatches;
  });
};
